// src/pages/DrillDownPage.js - Drill-down mid-page for KPI exploration
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronLeft, ChevronDown, FileText, MapPin, Users,
  Globe, Calendar, Layers, ArrowLeft, ArrowRight, AlertCircle,
  LayoutGrid, List
} from 'lucide-react';
import { apiService } from '../services/api';
import { formatNumber } from '../utils/localeFormatter';

const DIMENSION_CONFIG = {
  active_sites: { label: 'Active sites', labelBold: 'Sites', icon: MapPin, filterField: 'site_name', singular: 'site', unit: 'sites' },
  customers: { label: 'Customers', labelBold: 'Customers', icon: Users, filterField: 'customer_name', singular: 'customer', unit: 'customers' },
  countries: { label: 'Countries', labelBold: 'Countries', icon: Globe, filterField: 'country_name', singular: 'country', unit: 'countries' },
  report_types: { label: 'Report types', labelBold: 'Types', icon: Layers, filterField: 'report_type', singular: 'report type', unit: 'types' },
  this_month: { label: 'This month', labelBold: 'Records', icon: Calendar, filterField: 'this_month', singular: 'record', unit: 'records' },
};

const SORT_OPTIONS = [
  { value: 'reports_desc', label: 'Reports: High \u2192 Low' },
  { value: 'reports_asc', label: 'Reports: Low \u2192 High' },
  { value: 'name_asc', label: 'Name: A \u2192 Z' },
  { value: 'name_desc', label: 'Name: Z \u2192 A' },
];

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 ${className}`} />
);

// Volume badge derived from report count relative to max
const VolumeBadge = ({ count, max }) => {
  if (!max || max === 0) return null;
  const ratio = count / max;
  if (ratio >= 0.7) return <span className="text-[10px] font-semibold tracking-wider px-2 py-0.5 bg-gray-100 text-gray-700 border border-gray-200">High volume</span>;
  if (ratio >= 0.3) return <span className="text-[10px] font-semibold tracking-wider px-2 py-0.5 bg-gray-100 text-gray-600 border border-gray-200">Moderate</span>;
  return <span className="text-[10px] font-semibold tracking-wider px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200">Active</span>;
};

// Progress bar
const ReportBar = ({ count, max }) => {
  const pct = max > 0 ? Math.max((count / max) * 100, 2) : 0;
  return (
    <div className="w-full bg-gray-100 h-1.5 overflow-hidden">
      <div className="h-full bg-black transition-all duration-500" style={{ width: pct + '%' }} />
    </div>
  );
};

const DrillDownPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dimension = searchParams.get('dimension') || 'customers';
  const config = DIMENSION_CONFIG[dimension] || DIMENSION_CONFIG.customers;

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const searchTimeoutRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [sort, setSort] = useState('reports_desc');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Summary stats computed from response
  const [summaryStats, setSummaryStats] = useState({ totalItems: 0, totalReports: 0, maxReports: 0 });

  const fetchData = useCallback(async (searchTerm, pageNum, sortVal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDrillDown(dimension, searchTerm, pageNum, pageSize, sortVal);
      setItems(data.items || []);
      setTotalCount(data.total_count || 0);
      // Use API summary if available, else compute from items
      setSummaryStats({
        totalItems: data.total_count || 0,
        totalReports: data.total_reports || (data.items || []).reduce(function(s, i) { return s + (i.report_count || 0); }, 0),
        maxReports: data.max_report_count || Math.max.apply(null, (data.items || []).map(function(i) { return i.report_count || 0; }).concat([0])),
      });
    } catch (err) {
      setError(err.message || 'Failed to load data');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [dimension, pageSize]);

  useEffect(() => {
    setPage(1);
    setSearch('');
    setDebouncedSearch('');
  }, [dimension]);

  useEffect(() => {
    fetchData(debouncedSearch, page, sort);
  }, [debouncedSearch, page, sort, fetchData]);

  // Close sort dropdown on outside click
  useEffect(() => {
    const handler = function(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const handleItemClick = (item) => {
    const params = new URLSearchParams();
    params.set(config.filterField, item.name);
    navigate('/dashboard/reports?' + params.toString());
  };

  const handleSortChange = (val) => {
    setSort(val);
    setSortOpen(false);
    setPage(1);
  };

  const maxCount = useMemo(() => {
    return Math.max.apply(null, items.map(function(i) { return i.report_count || 0; }).concat([1]));
  }, [items]);

  const currentSortLabel = (SORT_OPTIONS.find(function(o) { return o.value === sort; }) || SORT_OPTIONS[0]).label;

  const pageStartIdx = (page - 1) * pageSize;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-1 text-sm text-gray-400 hover:text-black transition-colors mb-3"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Back</span>
          <span className="text-gray-300 mx-1">/</span>
          <span>{config.label}</span>
        </button>

        {/* Header row: Title left + Summary stats right */}
        <div className="flex items-start justify-between mb-6">
          <div className="text-left">
            <h1 className="text-4xl font-semibold text-gray-900 tracking-tight leading-none">
              {config.label.split(' ').length > 1
                ? <>{config.label.split(' ').slice(0, -1).join(' ')} <span className="text-black">{config.label.split(' ').slice(-1)}</span></>
                : <span className="text-black">{config.label}</span>
              }
            </h1>
            <p className="text-sm text-gray-500 mt-2">Click any {config.singular} to view its reports</p>
          </div>
          {!loading && (
            <div className="flex items-center space-x-3">
              <div className="border border-gray-200 bg-white px-5 py-3 text-center min-w-[112px] rounded-md">
                <p className="text-xl font-bold text-gray-900">{formatNumber(summaryStats.totalItems)}</p>
                <p className="text-[10px] font-semibold text-gray-400 tracking-wider">{config.unit}</p>
              </div>
              <div className="border border-gray-200 bg-white px-5 py-3 text-center min-w-[112px] rounded-md">
                <p className="text-xl font-bold text-gray-900">{formatNumber(summaryStats.totalReports)}</p>
                <p className="text-[10px] font-semibold text-gray-400 tracking-wider">Total reports</p>
              </div>
              <div className="border border-gray-200 bg-white px-5 py-3 text-center min-w-[112px] rounded-md">
                <p className="text-xl font-bold text-black">{formatNumber(summaryStats.maxReports)}</p>
                <p className="text-[10px] font-semibold text-gray-400 tracking-wider">Max reports</p>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar: View toggle + Sort + Search + Count */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            {/* View toggle */}
            <div className="flex border border-gray-200 bg-white rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={'p-2 transition-colors ' + (viewMode === 'grid' ? 'bg-black text-white' : 'text-gray-400 hover:text-gray-600')}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={'p-2 transition-colors ' + (viewMode === 'list' ? 'bg-black text-white' : 'text-gray-400 hover:text-gray-600')}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {/* Sort dropdown */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-200 bg-white text-sm text-gray-700 hover:border-gray-300 transition-colors rounded-md"
              >
                <span>{currentSortLabel}</span>
                <ChevronDown className={'h-3.5 w-3.5 text-gray-400 transition-transform ' + (sortOpen ? 'rotate-180' : '')} />
              </button>
              {sortOpen && (
                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 shadow-lg z-20 min-w-[200px]">
                  {SORT_OPTIONS.map(function(opt) {
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleSortChange(opt.value)}
                        className={'w-full text-left px-4 py-2.5 text-sm transition-colors ' + (sort === opt.value ? 'bg-black text-white font-medium' : 'text-gray-700 hover:bg-gray-50')}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={'Search...'}
                className="pl-9 pr-4 py-2 w-72 border border-gray-200 bg-white text-sm focus:outline-none focus:border-black transition-colors rounded-md"
              />
            </div>
          </div>

          <p className="text-sm text-gray-400 font-medium">
            {loading ? '...' : totalCount + ' ' + config.unit}
          </p>
        </div>

        {/* Content */}
        {loading ? (
          viewMode === 'list' ? (
            <div className="bg-white border border-gray-200 shadow-sm">
              <div className="px-5 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center">
                <span className="w-12 text-[11px] font-semibold text-gray-400">#</span>
                <span className="flex-1 text-[11px] font-semibold text-gray-400">Name</span>
                <span className="w-64 text-[11px] font-semibold text-gray-400">Reports</span>
                <span className="w-20 text-right text-[11px] font-semibold text-gray-400">Count</span>
                <span className="w-10" />
              </div>
              {Array.from({ length: 8 }).map(function(_, i) {
                return (
                  <div key={i} className="px-5 py-3.5 flex items-center border-b border-gray-50">
                    <div className="w-12"><Skeleton className="h-4 w-6" /></div>
                    <div className="flex-1"><Skeleton className="h-4 w-48" /></div>
                    <div className="w-64"><Skeleton className="h-2 w-full" /></div>
                    <div className="w-20 flex justify-end"><Skeleton className="h-4 w-10" /></div>
                    <div className="w-10" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map(function(_, i) {
                return (
                  <div key={i} className="bg-white border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <Skeleton className="h-5 w-10" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                    <Skeleton className="h-5 w-3/4 mb-4" />
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton className="h-3 w-16" />
                      <Skeleton className="h-4 w-10" />
                    </div>
                    <Skeleton className="h-1.5 w-full mb-4" />
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-6 w-6" />
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : error ? (
          <div className="bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center py-16">
            <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">Failed to load data</p>
            <p className="text-xs text-gray-400 mb-3">{error}</p>
            <button
              onClick={() => fetchData(debouncedSearch, page, sort)}
              className="px-4 py-1.5 text-xs font-medium bg-black text-white hover:bg-gray-800 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center py-16">
            <FileText className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm font-medium text-gray-600">No results found</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {search ? 'Try a different search term' : 'No data available'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* ========== LIST VIEW ========== */
          <div className="bg-white border border-gray-200 shadow-sm rounded-md overflow-hidden">
            {/* Column headers */}
            <div className="px-5 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center">
              <span className="w-12 text-[11px] font-semibold text-gray-400 tracking-wider">#</span>
              <span className="flex-1 text-[11px] font-semibold text-gray-400 tracking-wider">
                {dimension === 'active_sites' ? 'Site name' : dimension === 'customers' ? 'Customer name' : dimension === 'countries' ? 'Country' : dimension === 'report_types' ? 'Type' : 'Name'}
              </span>
              <span className="w-64 text-[11px] font-semibold text-gray-400 tracking-wider pl-4">Reports</span>
              <span className="w-10" />
            </div>

            {/* Rows */}
            {items.map(function(item, idx) {
              return (
                <div
                  key={item.name || idx}
                  onClick={() => handleItemClick(item)}
                  className="px-5 py-3.5 flex items-center border-b border-gray-100 cursor-pointer transition-colors group bg-white hover:bg-gray-50"
                >
                  <span className="w-12 text-xs text-gray-400 font-mono">
                    {String(pageStartIdx + idx + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-black truncate">{item.name}</p>
                  </div>
                  <div className="w-64 flex items-center space-x-3 pl-4">
                    <div className="flex-1">
                      <ReportBar count={item.report_count} max={maxCount} />
                    </div>
                    <span className="text-sm font-bold text-gray-700 w-14 text-right">{formatNumber(item.report_count || 0)}</span>
                  </div>
                  <div className="w-10 flex justify-end">
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ========== GRID VIEW ========== */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(function(item, idx) {
              return (
                <div
                  key={item.name || idx}
                  onClick={() => handleItemClick(item)}
                  className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer group rounded-md"
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        #{String(pageStartIdx + idx + 1).padStart(2, '0')}
                      </span>
                      <VolumeBadge count={item.report_count} max={maxCount} />
                    </div>

                    <h3 className="text-[30px] leading-none font-semibold text-gray-900 group-hover:text-black truncate mb-4">
                      {item.name}
                    </h3>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                      <span>Reports</span>
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.report_count || 0)}</span>
                    </div>
                    <ReportBar count={item.report_count} max={maxCount} />

                    <div className="flex items-center justify-between mt-5 pt-3 border-t border-gray-100">
                      {item.country ? (
                        <div className="flex items-center space-x-1.5 text-xs text-gray-500">
                          <MapPin className="h-3 w-3" />
                          <span>{item.country}</span>
                        </div>
                      ) : (
                        <div />
                      )}
                      <div className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded group-hover:border-gray-300 group-hover:bg-gray-50 transition-colors">
                        <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-700" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-xs text-gray-400">
              Showing {pageStartIdx + 1} of {Math.min(pageStartIdx + pageSize, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center space-x-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(1)}
                className="px-2.5 py-1.5 text-xs border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3 w-3" />
                <span>Prev</span>
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(totalPages, 5) }).map(function(_, i) {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={'px-3 py-1.5 text-xs border transition-colors ' + (pageNum === page ? 'bg-black text-white border-black font-medium' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="flex items-center space-x-1 px-2.5 py-1.5 text-xs border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <span>Next</span>
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
                className="px-2.5 py-1.5 text-xs border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrillDownPage;
